import React from 'react';
import { get, compact } from 'lodash';
import { ModelPreloader } from 'client/data/luckdragon/redux/model-preloader';
import { pageDefinition } from 'site-modules/shared/pages/page';
import { CmsModel } from 'client/data/models/cms';
import { removeSpecialCharacters } from 'site-modules/shared/utils/string';
import { getResearchCategoryName } from 'site-modules/editorial/utils/research-article';
import { MORE_ON_EDMUNDS_CONTENT_PATH, RESEARCH_SUBNAV_CONTENT_PATH } from 'client/data/constants/contents-paths';
import { getPageLinksPath, LinkWidgetsModel } from 'client/data/models/link-widgets';
import { getModelState } from 'client/data/luckdragon/model';
import { parseContent } from 'client/data/cms/content';
import { getBreadcrumbSchema } from 'client/seo/schemas/breadcrumbs';
import { getSeoObject } from 'site-modules/editorial/pages/research-category/seo';
import { getResearchCategoryLandingBreadcrumbs } from 'site-modules/editorial/utils/breadcrumbs';
import { getSeoImage } from 'site-modules/shared/utils/seo-helper';
import { getCategoryLandingContentPath } from 'site-modules/editorial/utils/content-path';

import { ResearchCategoryLanding } from './research-category-landing';

const PAGE_NAME_MAP = {
  'car-videos': 'video_index',
  'car-news': 'car_news_industry_article_index',
  'car-reviews_long-term-road-tests': 'car_reviews_longterm_roadtest_index',
  'car-reviews_features': 'car_reviews_features_index',
  'car-reviews_track-tests': 'car_reviews_tracktests_index',
};
const PAGE_CATEGORY_MAP = {
  'car-videos': 'video',
  'car-news': 'car-news',
  'car-reviews_long-term-road-tests': 'car_reviews',
  'car-reviews_features': 'car_reviews',
  'car-reviews_track-tests': 'car_reviews',
};

export const pageDefinitionConfiguration = (category, splat) => {
  const contentPath = getCategoryLandingContentPath(category, splat);
  const categorySplatName = compact([category, splat]).join('_');

  return {
    name: PAGE_NAME_MAP[categorySplatName] || `advice_${getResearchCategoryName(category)}_article_index`,
    category: PAGE_CATEGORY_MAP[categorySplatName] || 'advice',
    adsCustomTargeting: {
      sect1: removeSpecialCharacters(category),
    },
    preload: store => {
      const preloader = new ModelPreloader(store);

      preloader.resolve(`content["${contentPath}"]`, CmsModel);
      preloader.resolve(getPageLinksPath(), LinkWidgetsModel);
      preloader.resolve(`content["${MORE_ON_EDMUNDS_CONTENT_PATH}"]`, CmsModel);
      preloader.resolve(`content["${RESEARCH_SUBNAV_CONTENT_PATH}"]`, CmsModel);

      return preloader.load();
    },
    seo: state => {
      const content = parseContent(getModelState(state).get(`content["${contentPath}"]`, CmsModel));
      const title = get(content, 'title', '');
      const breadcrumbTitle = content.metadata('breadcrumbTitle').value(title);
      const seoObject = getSeoObject(
        state,
        contentPath,
        null,
        getBreadcrumbSchema(getResearchCategoryLandingBreadcrumbs({ category, splat, title: breadcrumbTitle }))
      );

      return {
        ...seoObject,
        image: getSeoImage(content),
      };
    },
    component: props => <ResearchCategoryLanding category={category} splat={splat} {...props} />,
    options: {
      isEditorialPageWithInlineMobileAd: true,
    },
  };
};

/**
 * Returns constructor for dynamic page definition.
 *
 * @param  {Object} options
 * @return {Object} page definition for this article.
 *
 * Simple memoization added to prevent page rerender
 * Generated pageDefinitions added to array
 * Example: pages['car-buying']
 */
const pages = new Map();
export default ({ articleCategory, splat }) => {
  const key = `${articleCategory}_${splat}`;
  let page = pages.get(key);
  if (!page) {
    page = pageDefinition(pageDefinitionConfiguration(articleCategory, splat));
    pages.set(key, page);
  }
  return page;
};
